import { Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { iif, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Campaign } from 'src/app/models/campaign';
import { ToggleHide } from 'src/app/shared/ui/spinner/redux/spinner.actions';
import { ReloadContent } from '../../content/redux';
import { LoadInsightsInactive, ReloadInsights } from '../../insights/redux';
import { ReloadSurveys } from '../../surveys/redux';
import { CampaignService } from '../services/campaign.service';
import { CampaignStateModel } from './campaign-state.model';
import {
  AddCampaignJob,
  AppendCampaignEventTriggers,
  DeepDeleteCampaign,
  DeepDeleteCampaignSuccess,
  LoadAffectedCampaigns,
  LoadAffectedCampaignsByInsight,
  LoadAffectedCampaignsSuccess,
  LoadCampaigns,
  LoadCampaignsSuccess,
  LoadMyCampaigns,
  LoadMyCampaignsSuccess,
  LoadQuickstartDeliveries,
  LoadQuickstartDeliveriesSuccess,
  ManageCampaign,
  PopulateCampaignEventTriggers,
  PostCampaign,
  PostSaveCampaignComplete,
  PostSaveCampaignFail,
  ReloadCampaign,
  ReloadCampaignSuccess,
  RemoveCampaignJob,
  SaveCampaignDeliveries
} from './campaign.actions';

@State<CampaignStateModel>({
  name: 'campaign',
  defaults: {
    campaign: undefined,
    myCampaigns: undefined,
    campaignDeliveries: [],
    quickstartDeliveries: {},
    campaignEventTriggers: [],
    error: null,
    campaignJobs: [],
    promotion: null,
    affectedCampaigns: undefined,
    spin: true
  }
})
@Injectable()
export class CampaignState {
  constructor(
    private toastrService: NbToastrService,
    private service: CampaignService
  ) {}

  @Selector()
  public static campaign(state: CampaignStateModel) {
    return state.campaign;
  }

  @Selector()
  public static myCampaigns(state: CampaignStateModel) {
    return state.myCampaigns;
  }

  @Selector()
  public static deliveries(state: CampaignStateModel) {
    return state.campaignDeliveries;
  }

  @Selector()
  public static quickstartDeliveries(state: CampaignStateModel) {
    return state.quickstartDeliveries;
  }

  @Selector()
  public static eventTriggers(state: CampaignStateModel) {
    return state.campaignEventTriggers;
  }

  @Selector()
  public static campaignJobs(state: CampaignStateModel) {
    return state.campaignJobs;
  }

  @Selector()
  public static PromotionSuccess(state: CampaignStateModel) {
    return state.promotion;
  }

  @Selector()
  public static affectedCampaigns(state: CampaignStateModel) {
    return state.affectedCampaigns;
  }

  @Selector()
  public static spin(state: CampaignStateModel) {
    return state.spin;
  }

  @Action(LoadCampaigns)
  public async loadCampaign() {
    this.service.loadCampaignsAmplify();
  }

  @Action(LoadCampaigns)
  public async PromotionSuccess() {}

  @Action(LoadCampaignsSuccess)
  public loadCampaignSuccess(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      campaign: payload.campaigns
    });
    ctx.dispatch(ToggleHide);
    ctx.dispatch(new PopulateCampaignEventTriggers(payload.campaigns));
  }

  @Action(LoadMyCampaigns)
  public async loadMyCampaigns(_ctx: StateContext<CampaignStateModel>, { email }: any) {
    this.service.loadMyCampaignsAmplify(email);
  }

  @Action(LoadMyCampaignsSuccess)
  public loadMyCampaignsSuccess(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      myCampaigns: payload.campaigns
    });
    ctx.dispatch(ToggleHide);
  }

  @Action(LoadAffectedCampaigns)
  public loadAffectedCampaigns(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      spin: true
    });
    this.service.loadAffectedCampaignsAmplify(payload);
  }

  @Action(LoadAffectedCampaignsSuccess)
  public loadAffectedCampaignsSuccess(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      affectedCampaigns: payload,
      spin: false
    });
  }

  @Action(LoadAffectedCampaignsByInsight)
  public LoadAffectedCampaignsByInsight(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      spin: true
    });
    this.service.loadAffectedCampaignsByInsightAmplify(payload);
  }

  @Action(ReloadCampaign)
  public async reloadCampaign() {
    this.toastrService.show(status, $localize`Fetch Initiated`, { status: 'info' });
    this.service.loadCampaignsAmplify(true);
  }

  @Action(ReloadCampaignSuccess)
  public reloadCampaignSuccess(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      campaign: payload
    });
    ctx.dispatch(new PopulateCampaignEventTriggers(payload.campaigns));
  }

  @Action(PostCampaign)
  public async postCampaign(_ctx: StateContext<CampaignStateModel>, { payload, edit }: any) {
    this.service.postCampaignsAmplify(payload, edit);
  }

  @Action(DeepDeleteCampaign)
  public async deepDeleteCampaign(_ctx: StateContext<CampaignStateModel>, { payload }: any) {
    this.service.deepDeleteCampaignAmplify(payload);
  }

  @Action(DeepDeleteCampaignSuccess)
  public deepDeleteCampaignSuccess(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    ctx.setState(
      patch({
        campaign: removeItem((item: any) => item.id === payload.campaign_id)
      })
    );
    ctx.dispatch(ReloadInsights);
    ctx.dispatch(LoadInsightsInactive);
    ctx.dispatch(ReloadContent);
    ctx.dispatch(ReloadSurveys);
  }

  @Action(PostSaveCampaignComplete)
  public campaignComplete(ctx: StateContext<CampaignStateModel>, { data, campaign }: any) {
    // Data can contain "true" when its updating so we update item with "campaign" which is local copy
    // In case you make new "campaign" with post it will return new Object with "data" so in second case
    // We append it to last place in array with data
    const state = ctx.getState();
    if (data === true) {
      ctx.setState(
        patch({
          campaign: updateItem((item) => item.id === campaign.id, campaign)
        })
      );
      this.toastrService.show(status, $localize`Edit: Complete`, { status: 'success' });
    }
    if (data === undefined) {
      ctx.setState(
        patch({
          campaign: [...state.campaign, campaign]
        })
      );
      this.toastrService.show(status, $localize`Save: Complete`, { status: 'success' });
    }
  }

  @Action(PostSaveCampaignFail)
  public campaignFail(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      error: payload.message
    });
    this.toastrService.show(status, $localize`Save: Fail - ${payload.message}`, { status: 'danger' });
  }

  @Action(SaveCampaignDeliveries)
  public campaignDeliveries(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    ctx.setState(
      patch<CampaignStateModel>({
        campaignDeliveries: iif<any[]>(
          (campaignDeliveries) => campaignDeliveries.some((f) => f.campaign_id === payload.campaign_id),
          updateItem<any>((f) => f.campaign_id === payload.campaign_id, payload),
          insertItem(payload)
        )
      })
    );
  }

  @Action(PopulateCampaignEventTriggers)
  public populateCampaignEventTriggers(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const arr: string[] = [];
    payload.forEach((element: Campaign) => {
      if (element.event_triggers) {
        if (element.event_triggers instanceof Array) {
          element.event_triggers.forEach((element) => {
            arr.push(element);
          });
        } else {
          arr.push(element.event_triggers);
        }
      }
    });

    const unique = [...new Set(arr.map((item) => item))];
    ctx.setState(
      patch({
        campaignEventTriggers: unique
      })
    );
  }

  @Action(AppendCampaignEventTriggers)
  public appendCampaignEventTriggers(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    const state = ctx.getState();
    let arr = state.campaignEventTriggers;
    arr = Object.assign([], arr);
    if (payload instanceof Array) {
      payload.forEach((element) => {
        arr.push(element);
      });
    } else {
      arr.push(payload);
    }

    const unique = [...new Set(arr.map((item: any) => item))];
    ctx.setState(
      patch({
        campaignEventTriggers: unique
      })
    );
  }

  @Action(AddCampaignJob)
  public campaignJobInsert(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    ctx.setState(
      patch<CampaignStateModel>({
        campaignJobs: iif<any[]>(
          (campaignJobs) => campaignJobs.some((f) => f.campaignId === payload.campaignId),
          updateItem<any>((f) => f.campaignId === payload.campaignId, payload),
          insertItem(payload)
        )
      })
    );
  }

  @Action(RemoveCampaignJob)
  public campaignJobRemove(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    ctx.setState(
      patch({
        campaignJobs: removeItem((item) => item.job_id === payload)
      })
    );
  }

  @Action(ManageCampaign)
  public activateCampaign(ctx: StateContext<CampaignStateModel>, { payload }: any) {
    ctx.setState(
      patch({
        campaign: updateItem((item) => item.id === payload.id, payload)
      })
    );
  }

  @Action(LoadQuickstartDeliveriesSuccess)
  public saveQuickstartDeliveries(ctx: StateContext<CampaignStateModel>, { payload }: LoadQuickstartDeliveriesSuccess) {
    const state = ctx.getState();

    ctx.setState(
      patch({
        quickstartDeliveries: {
          ...state.quickstartDeliveries,
          ...payload
        }
      })
    );
  }

  @Action(LoadQuickstartDeliveries)
  public async LoadQuickstartDeliveries(
    ctx: StateContext<CampaignStateModel>,
    { campaignIds, startDate, endDate, reload }: LoadQuickstartDeliveries
  ) {
    this.service.loadQuickstartDeliveriesAmplify(campaignIds, startDate, endDate, reload);
  }
}
